import React, { useState, useEffect, useMemo, useRef } from "react";
import { Icon } from "@iconify/react";
import PageHeader from "./PageHeader";

const DataTable = ({
  title,
  subtitle,
  data = [],
  columns = [],
  actions = [],
  bulkActions = [],
  rowKey = "id",
  searchTerm = "",
  onSearchChange = () => {},
  page = 1,
  limit = 10,
  total = 0,
  onPageChange,
  onLimitChange,
  pagination = true,
  loading = false,
  headerActions = [],
  showCheckbox = true,
  sortConfig = { key: null, direction: 'asc' },
  onSortChange,
  storageKey = null,
  enableColumnCustomizer = true,
  filterComponent = null,
}) => {
  /* ================= COLUMN CUSTOMIZER & ORDERING ================= */
  const [isColumnDropdownOpen, setIsColumnDropdownOpen] = useState(false);
  const [columnSearch, setColumnSearch] = useState("");
  const dropdownRef = useRef(null);

  // Initialize columns ordering & visibility
  const [columnsOrder, setColumnsOrder] = useState(() => {
    try {
      if (storageKey) {
        const saved = localStorage.getItem(storageKey);
        if (saved) {
          const parsed = JSON.parse(saved);
          if (Array.isArray(parsed) && parsed.length > 0) {
            const savedKeys = new Set(parsed.map((c) => c.key));
            const missing = columns.filter((c) => !savedKeys.has(c.key)).map((c) => ({
              key: c.key,
              visible: c.defaultVisible !== false,
            }));
            return [...parsed, ...missing];
          }
        }
      }
    } catch (e) {
      console.error("Failed to parse column preferences:", e);
    }
    return columns.map((c) => ({ key: c.key, visible: c.defaultVisible !== false }));
  });

  // Sync columns if columns prop changes length or keys
  useEffect(() => {
    setColumnsOrder((prev) => {
      const prevKeys = new Set(prev.map((c) => c.key));
      const newItems = columns.filter((c) => !prevKeys.has(c.key)).map((c) => ({
        key: c.key,
        visible: c.defaultVisible !== false,
      }));
      const currentPropKeys = new Set(columns.map((c) => c.key));
      const existingFiltered = prev.filter((c) => currentPropKeys.has(c.key));
      if (newItems.length === 0 && existingFiltered.length === prev.length) {
        return prev;
      }
      return [...existingFiltered, ...newItems];
    });
  }, [columns]);

  // Persist to localStorage
  useEffect(() => {
    if (storageKey && columnsOrder.length > 0) {
      try {
        localStorage.setItem(storageKey, JSON.stringify(columnsOrder));
      } catch (e) {
        console.error("Failed to store column preferences:", e);
      }
    }
  }, [columnsOrder, storageKey]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsColumnDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const colMap = useMemo(() => {
    const m = new Map();
    columns.forEach((c) => m.set(c.key, c));
    return m;
  }, [columns]);

  const visibleColumns = useMemo(() => {
    if (!enableColumnCustomizer) return columns;
    return columnsOrder
      .filter((c) => c.visible)
      .map((c) => colMap.get(c.key))
      .filter(Boolean);
  }, [columnsOrder, colMap, enableColumnCustomizer, columns]);

  const handleMoveColumn = (index, direction, e) => {
    e.stopPropagation();
    const newOrder = [...columnsOrder];
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newOrder.length) return;

    const [moved] = newOrder.splice(index, 1);
    newOrder.splice(targetIndex, 0, moved);
    setColumnsOrder(newOrder);
  };

  const handleToggleColumn = (key) => {
    setColumnsOrder((prev) =>
      prev.map((c) => (c.key === key ? { ...c, visible: !c.visible } : c))
    );
  };

  const handleSelectAllColumns = (visible) => {
    setColumnsOrder((prev) => prev.map((c) => ({ ...c, visible })));
  };

  const handleResetColumns = () => {
    setColumnsOrder(columns.map((c) => ({ key: c.key, visible: c.defaultVisible !== false })));
  };

  const filteredColumnsForMenu = useMemo(() => {
    if (!columnSearch.trim()) return columnsOrder;
    const q = columnSearch.toLowerCase();
    return columnsOrder.filter((c) => {
      const def = colMap.get(c.key);
      return (
        (def?.label || "").toLowerCase().includes(q) ||
        (def?.category || "").toLowerCase().includes(q)
      );
    });
  }, [columnsOrder, columnSearch, colMap]);

  /* ================= CALCULATIONS ================= */
  const totalColSpan = visibleColumns.length + (showCheckbox ? 1 : 0) + (actions.length > 0 ? 1 : 0);
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const [selectedRows, setSelectedRows] = React.useState([]);

  /* ================= PAGINATION LOGIC ================= */
  const getPaginationGroup = () => {
    const pageRange = 2;
    let start = Math.max(1, page - pageRange);
    let end = Math.min(totalPages, page + pageRange);

    const pages = [];
    if (start > 1) {
      pages.push(1);
      if (start > 2) pages.push("...");
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (end < totalPages) {
      if (end < totalPages - 1) pages.push("...");
      pages.push(totalPages);
    }

    return pages;
  };

  /* ================= HANDLERS ================= */
  const toggleRow = (id) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleAll = () => {
    if (selectedRows.length === data.length && data.length > 0) {
      setSelectedRows([]);
    } else {
      setSelectedRows(data.map((row) => row[rowKey]));
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    if (onSortChange) onSortChange({ key, direction });
  };

  return (
    <>
      {title && <PageHeader title={title} subtitle={subtitle} />}
      <div className="card radius-12 border-0 shadow-none bg-base">
        <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-3 bg-base border-bottom">
          <div className="d-flex flex-wrap align-items-center gap-3">
            {pagination && (
              <div className="d-flex align-items-center gap-2">
                <span className="text-secondary-light text-xs text-nowrap">Show</span>
                <select
                  className="form-select form-select-sm w-auto bg-base border text-secondary-light text-xs"
                  value={limit}
                  onChange={(e) => onLimitChange && onLimitChange(Number(e.target.value))}
                >
                  {[10, 25, 50, 100].map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="icon-field">
              <input
                type="text"
                className="form-control form-control-sm text-xs w-auto"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => onSearchChange(e.target.value)}
              />
              <span className="icon"><Icon icon="ion:search-outline" /></span>
            </div>

            {filterComponent}

            {showCheckbox && selectedRows.length > 0 && bulkActions.length > 0 && (
              <div className="d-flex align-items-center gap-2 border-start ps-3 ms-2">
                <span className="text-xs fw-bold text-primary-600 text-nowrap">{selectedRows.length} selected</span>
                {bulkActions.map((action, i) => (
                  <button
                    key={i}
                    className={`btn btn-sm ${action.className} radius-8 py-1 px-3 d-flex align-items-center gap-2 text-xs`}
                    onClick={() => {
                      action.onClick(selectedRows);
                      setSelectedRows([]);
                    }}
                  >
                    {action.icon && <Icon icon={action.icon} />}
                    {action.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="d-flex flex-wrap align-items-center gap-3">
            {/* Column Customizer Dropdown */}
            {enableColumnCustomizer && (
              <div className="position-relative" ref={dropdownRef}>
                <button
                  type="button"
                  className={`btn btn-sm radius-8 d-flex align-items-center gap-2 text-xs ${
                    isColumnDropdownOpen ? "btn-primary" : "btn-outline-neutral-300 text-neutral-700 bg-white"
                  }`}
                  onClick={() => setIsColumnDropdownOpen((prev) => !prev)}
                >
                  <Icon icon="solar:settings-minimalistic-linear" className="text-sm" />
                  <span>Columns ({visibleColumns.length}/{columns.length})</span>
                  <Icon icon={isColumnDropdownOpen ? "solar:alt-arrow-up-linear" : "solar:alt-arrow-down-linear"} className="text-xs" />
                </button>

                {isColumnDropdownOpen && (
                  <div
                    className="dropdown-menu show shadow-lg border-0 radius-12 p-16 position-absolute end-0 mt-2 bg-white"
                    style={{ width: "340px", maxHeight: "460px", zIndex: 1050 }}
                  >
                    <div className="d-flex align-items-center justify-content-between mb-12">
                      <h6 className="text-sm fw-bold mb-0 text-neutral-800">Select & Order Columns</h6>
                      <button
                        type="button"
                        className="btn btn-link p-0 text-xs text-primary-600 text-decoration-none"
                        onClick={handleResetColumns}
                      >
                        Reset Order
                      </button>
                    </div>

                    {/* Column Search */}
                    <div className="mb-12">
                      <input
                        type="text"
                        className="form-control form-control-sm text-xs radius-8"
                        placeholder="Search columns..."
                        value={columnSearch}
                        onChange={(e) => setColumnSearch(e.target.value)}
                        autoFocus
                      />
                    </div>

                    {/* Bulk Actions */}
                    <div className="d-flex align-items-center justify-content-between mb-8 pb-8 border-bottom">
                      <button
                        type="button"
                        className="btn btn-xs btn-link text-primary-600 text-decoration-none p-0"
                        onClick={() => handleSelectAllColumns(true)}
                      >
                        Select All
                      </button>
                      <button
                        type="button"
                        className="btn btn-xs btn-link text-secondary-light text-decoration-none p-0"
                        onClick={() => handleSelectAllColumns(false)}
                      >
                        Hide All
                      </button>
                    </div>

                    {/* Column List with Up/Down and Checkbox */}
                    <div style={{ maxHeight: "240px", overflowY: "auto" }} className="pe-1">
                      {filteredColumnsForMenu.map((c, index) => {
                        const colDef = colMap.get(c.key);
                        if (!colDef) return null;

                        const isFirst = index === 0;
                        const isLast = index === filteredColumnsForMenu.length - 1;

                        return (
                          <div
                            key={c.key}
                            className={`d-flex align-items-center justify-content-between py-6 px-8 radius-6 mb-2 ${
                              c.visible ? "bg-neutral-50" : "opacity-75"
                            }`}
                          >
                            <div className="form-check d-flex align-items-center gap-2 mb-0">
                              <input
                                className="form-check-input mt-0"
                                type="checkbox"
                                id={`dt-col-${c.key}`}
                                checked={c.visible}
                                onChange={() => handleToggleColumn(c.key)}
                              />
                              <label
                                className="form-check-label text-xs fw-medium text-neutral-800 cursor-pointer user-select-none"
                                htmlFor={`dt-col-${c.key}`}
                              >
                                {colDef.label}
                              </label>
                            </div>

                            {/* Order buttons */}
                            <div className="d-flex align-items-center gap-1">
                              {colDef.category && (
                                <span className="badge bg-light text-secondary-light text-2xs me-1">
                                  {colDef.category}
                                </span>
                              )}
                              <button
                                type="button"
                                className="btn btn-xs btn-light p-1 radius-4"
                                disabled={isFirst}
                                onClick={(e) => handleMoveColumn(index, "up", e)}
                                title="Move Up"
                              >
                                <Icon icon="mingcute:arrow-up-line" style={{ fontSize: "12px" }} />
                              </button>
                              <button
                                type="button"
                                className="btn btn-xs btn-light p-1 radius-4"
                                disabled={isLast}
                                onClick={(e) => handleMoveColumn(index, "down", e)}
                                title="Move Down"
                              >
                                <Icon icon="mingcute:arrow-down-line" style={{ fontSize: "12px" }} />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {headerActions.map((action, i) => (
              <button key={i} className={`btn btn-sm ${action.className} text-xs`} onClick={action.onClick}>
                {action.icon && <Icon icon={action.icon} />} {action.label}
              </button>
            ))}
          </div>
        </div>

        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover borderless-table table-sm mb-0 align-middle text-xs">
              <thead className="bg-neutral-100">
                <tr>
                  {showCheckbox && (
                    <th className="text-secondary-dark fw-bold text-xs uppercase px-12 py-10" style={{ width: "60px" }}>
                      <div className="form-check style-check d-flex align-items-center">
                        <input className="form-check-input" type="checkbox" checked={data.length > 0 && selectedRows.length === data.length} onChange={toggleAll} />
                        <label className="form-check-label text-xs fw-bold">S.L</label>
                      </div>
                    </th>
                  )}
                  {visibleColumns.map((col) => (
                    <th 
                      key={col.key} 
                      onClick={() => col.sortable !== false && handleSort(col.key)} 
                      className={`text-secondary-dark fw-bold uppercase text-xs px-12 py-10 user-select-none ${col.sortable !== false ? 'cursor-pointer' : ''}`}
                    >
                      <div className="d-flex align-items-center gap-2">
                        <span>{col.label}</span>
                        {col.sortable !== false && (
                          <Icon 
                            icon={sortConfig.key === col.key ? (sortConfig.direction === 'asc' ? "lucide:arrow-up" : "lucide:arrow-down") : "lucide:chevrons-up-down"} 
                            className={sortConfig.key === col.key ? "text-primary-600 text-xs" : "text-neutral-400 text-xs"} 
                          />
                        )}
                      </div>
                    </th>
                  ))}
                  {actions.length > 0 && <th className="text-secondary-dark fw-bold text-xs uppercase px-12 py-10 text-end" style={{ width: "120px" }}>Action</th>}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={totalColSpan} className="text-center py-80">
                      <Icon icon="line-md:loading-twotone-loop" className="text-primary-600 text-3xl mb-2" />
                      <p className="text-secondary-light text-xs">Fetching data...</p>
                    </td>
                  </tr>
                ) : data.length === 0 ? (
                  <tr><td colSpan={totalColSpan} className="text-center py-80 text-secondary-light text-xs">No records found.</td></tr>
                ) : (
                  data.map((row, idx) => (
                    <tr key={row[rowKey] ?? idx}>
                      {showCheckbox && (
                        <td className="text-xs px-12 py-8 align-middle">
                          <div className="form-check style-check d-flex align-items-center">
                            <input className="form-check-input" type="checkbox" checked={selectedRows.includes(row[rowKey])} onChange={() => toggleRow(row[rowKey])} />
                            <label className="form-check-label text-xs">{String((page - 1) * limit + (idx + 1)).padStart(2, "0")}</label>
                          </div>
                        </td>
                      )}
                      {visibleColumns.map((col) => (
                        <td key={col.key} className={`text-xs px-12 py-8 align-middle ${col.className || ''}`}>
                          {col.render ? col.render(row) : (row[col.key] ?? "--")}
                        </td>
                      ))}
                      {actions.length > 0 && (
                        <td className="text-end px-12 py-8 align-middle">
                          <div className="d-flex align-items-center justify-content-end gap-2">
                            {actions.map((action, i) => (
                              <button 
                                key={i} 
                                type="button" 
                                onClick={() => action.onClick(row)} 
                                className={`w-28-px h-28-px border-0 rounded d-flex align-items-center justify-content-center text-xs transition-all ${action.className}`}
                                title={action.title || action.label}
                              >
                                <Icon icon={action.icon} className="text-xs" />
                              </button>
                            ))}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* FOOTER: Pagination */}
        {pagination && !loading && data.length > 0 && (
          <div className="card-footer d-flex flex-wrap align-items-center justify-content-between bg-base border-top py-10 px-20">
            <span className="text-secondary-light text-xs">
              Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of {total} entries
            </span>
            <ul className="pagination mb-0 gap-1">
              <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                <button 
                  className="page-link border-0 bg-neutral-100 radius-8 text-xs px-10 py-6 d-flex align-items-center justify-content-center" 
                  onClick={() => page > 1 && onPageChange(page - 1)}
                  disabled={page === 1}
                >
                  <Icon icon="lucide:chevron-left" className="text-xs" />
                </button>
              </li>
              
              {getPaginationGroup().map((item, i) => (
                <li key={i} className={`page-item ${page === item ? 'active' : ''} ${item === "..." ? 'disabled' : ''}`}>
                  {item === "..." ? (
                    <span className="page-link border-0 bg-neutral-100 radius-8 text-xs px-10 py-6">...</span>
                  ) : (
                    <button 
                      className={`page-link border-0 radius-8 text-xs px-10 py-6 ${page === item ? 'bg-primary-600 text-white' : 'bg-neutral-100 text-secondary-light'}`} 
                      onClick={() => onPageChange(item)}
                    >
                      {item}
                    </button>
                  )}
                </li>
              ))}

              <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                <button 
                  className="page-link border-0 bg-neutral-100 radius-8 text-xs px-10 py-6 d-flex align-items-center justify-content-center" 
                  onClick={() => page < totalPages && onPageChange(page + 1)}
                  disabled={page === totalPages}
                >
                  <Icon icon="lucide:chevron-right" className="text-xs" />
                </button>
              </li>
            </ul>
          </div>
        )}
      </div>
    </>
  );
};

export default DataTable;